import React, { useEffect, useRef, useState, useContext } from "react";
import { Checkbox, Input } from "components/form-inputs/inputs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { useAttributes } from "shared/hooks/api/use-attributes";
import ReactTable from "react-table";
import { useForm, Controller } from "react-hook-form";
import { useUpdateAttribute } from "shared/hooks/api/use-update-attribute";
import { useBulkDownloadAttributes } from "shared/hooks/api/use-bulk-download-attributes";
import * as notify from "shared/notify";

import "../clients/clients.scss";
import { useUploadAttribute } from "../../shared/hooks/api/use-upload-attribute";
import { buildColumns } from "./lib/attribute-list-columns";
import { ViewOnlyPermissionsContext } from "contexts/view-only-permissions/view-only-permissions";

const AttributeSettings = () => {
    const { viewOnlyAdmin } = useContext(ViewOnlyPermissionsContext);
    const fileInputRef = useRef();
    const [searchValue, setSearchValue] = useState("");
    const [toggleSearchValue, setToggleSearchValue] = useState();
    const [rowDetails, setRowDetails] = useState();
    const { data: attributes, isLoading, refetch } = useAttributes(toggleSearchValue, "adminattributesettings");
    const { mutate: downloadAttributes, isLoading: isDownloadingAttributes } = useBulkDownloadAttributes(searchValue);
    const { mutate: uploadFile, isLoading: isUploadingFile } = useUploadAttribute();
    const { mutate: updateAttribute, isLoading: isUpdating } = useUpdateAttribute();
    const { reset, handleSubmit, control } = useForm();

    useEffect(() => {
        if (rowDetails) {
            reset({
                attributeName: rowDetails.name,
                uuid: rowDetails.uuid,
                componentType: rowDetails.componentType,
                hidden: rowDetails.hidden,
                textTransform: rowDetails.textTransform,
                minChars: rowDetails.minChars,
                maxChars: rowDetails.maxChars,
                shortDescription: rowDetails.shortDescription,
                tooltip: rowDetails.tooltip,
            });
        }
    }, [rowDetails]);

    const onFileChange = () => {
        const file = fileInputRef.current.files[0];

        uploadFile(
            { file },
            {
                onSuccess: (data) => {
                    if (data) {
                        notify.success("Attributes uploaded");
                    }

                    if (searchValue) {
                        refetch();
                    }

                    fileInputRef.current.value = "";
                },
                onError: () => {
                    fileInputRef.current.value = "";
                },
            }
        );
    };

    const onSubmit = async ({ uuid, shortDescription, tooltip }) => {
        updateAttribute(
            [
                {
                    Uuid: uuid,
                    HelpText: shortDescription,
                    Tooltip: tooltip,
                },
            ],
            {
                onSuccess: () => {
                    notify.success("Attribute updated");
                    refetch();
                },
            }
        );
    };

    const handleSearch = () => {
        setToggleSearchValue(searchValue);
        setRowDetails(null);
    };

    const handleEnter = (e) => {
        if (e.key === "Enter") {
            setToggleSearchValue(searchValue);
            setRowDetails(null);
        }
    };

    const onRowClick = (s, rowInfo) => {
        return {
            onClick: () => setRowDetails(rowInfo.original),
            style: {
                cursor: "pointer",
                backgroundColor: rowDetails && rowInfo && rowInfo.original && rowInfo.original.key === rowDetails.key ? "#f9de473e" : "#ffffff",
            },
        };
    };

    return (
        <>
            <div className="justify-content-between">
                <div className="flex-column flex-1 m-r-28 p-8">
                    <h1>Attribute Settings</h1>
                    <div className="align-items-end">
                        <Input
                            className="flex-1"
                            name="searchAttribute"
                            placeholder="Search attribute..."
                            value={searchValue}
                            onCancel={() => setSearchValue("")}
                            onKeyUp={(e) => setSearchValue(e.target.value)}
                            onKeyPress={handleEnter}
                        />
                        <button className="button -primary" onClick={handleSearch}>
                            Search attribute
                        </button>
                    </div>
                </div>
                <div className="flex-column flex-1">
                    <div className="button-group justify-content-end m-t-16">
                        <button className="button -primary" disabled={isDownloadingAttributes} onClick={() => downloadAttributes({ searchValue })}>
                            <FontAwesomeIcon icon={faDownload} />
                        </button>
                        <label className="upload-button" disabled={viewOnlyAdmin.uploadAttributes}>
                            <input
                                className="upload-file"
                                type="file"
                                onChange={onFileChange}
                                accept=".csv"
                                ref={fileInputRef}
                                disabled={isUploadingFile || viewOnlyAdmin.uploadAttributes}
                            />
                            Upload
                        </label>
                    </div>
                </div>
            </div>
            <div className="flex-column">
                <div className="justify-content-between">
                    <div className="flex-1 m-r-8 p-8">
                        <ReactTable
                            loading={isLoading}
                            data={attributes}
                            columns={buildColumns()}
                            minRows="5"
                            getTdProps={onRowClick}
                            showPageJump={false}
                        />
                    </div>

                    <div className={`flex-1 m-8 ${rowDetails ? "border" : ""}`}>
                        {rowDetails && (
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="align-items-center justify-content-between form-info p-l-16 p-r-16 m-b-8">
                                    <h3>Attribute information</h3>
                                    <button type="submit" className="button -primary" disabled={isUpdating || viewOnlyAdmin.updateAttributes}>
                                        Save
                                    </button>
                                </div>
                                <div className="p-l-16 p-r-16">
                                    <Controller
                                        name="attributeName"
                                        control={control}
                                        render={({ field }) => <Input {...field} disabled={true} label="Attribute name" />}
                                    />
                                    <Controller
                                        name="uuid"
                                        control={control}
                                        render={({ field }) => <Input {...field} disabled={true} label="UUID" />}
                                    />
                                    <Controller
                                        name="componentType"
                                        control={control}
                                        render={({ field }) => <Input {...field} disabled={true} label="Data type" />}
                                    />
                                    <Controller
                                        name="hidden"
                                        control={control}
                                        render={({ field }) => <Checkbox {...field} disabled={true} label="Hidden" />}
                                    />
                                    <Controller
                                        name="textTransform"
                                        control={control}
                                        render={({ field }) => <Input {...field} disabled={true} label="Text transform" />}
                                    />
                                    <div className="flex">
                                        <div className="flex-1 m-r-8">
                                            <Controller
                                                name="minChars"
                                                control={control}
                                                render={({ field }) => <Input {...field} disabled={true} label="Min chars" />}
                                            />
                                        </div>
                                        <div className="flex-1 m-l-8">
                                            <Controller
                                                name="maxChars"
                                                control={control}
                                                render={({ field }) => <Input {...field} disabled={true} label="Max chars" />}
                                            />
                                        </div>
                                    </div>
                                    <Controller
                                        name="shortDescription"
                                        control={control}
                                        render={({ field }) => <Input {...field} disabled={viewOnlyAdmin.viewAttributes} label="Description" />}
                                    />
                                    <Controller
                                        name="tooltip"
                                        control={control}
                                        render={({ field }) => <Input {...field} disabled={viewOnlyAdmin.viewAttributes} label="Tooltip" />}
                                    />
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AttributeSettings;
